/**
 * CMS 자동이체 신청서 작성 모달 ($j.Ui.JBModal.target 결합).
 * 실제 양식(public/img/adm/cms_form.png, 2481x3509 A4 — 거래은행 칸이 비어있는 빈 양식) 위에
 * 입력값을 좌표 오버레이로 얹어 html2canvas 로 합성 → JPG 다운로드한다.
 * 거래은행은 양식에 인쇄돼 있지 않으므로 목록 전체(□은행명)를 직접 그리고, 선택 은행만 ☑ 로 표시한다.
 *
 * 사용법:
 *   $j.Ui.JBModal.target('cms-form-modal', {
 *       target: '$j.F.Admin.Member.CmsForm',
 *       data:   { customerNo, applicantName, phone, mobile, bank, account, holderName, rrn, holderType, applyDate },
 *       title:  'CMS 자동이체 신청서',
 *       width:  '560px',
 *   });
 *
 * ⚠ 오버레이 좌표(POS/BANK_*)는 양식 이미지 기준 %. 실제 출력 PNG 와 비교해 미세조정한다.
 */
import Util from '../../../Common/Util';

const FORM_IMG  = '/img/adm/cms_form.png';
const H2C_SRC   = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js';
const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Nanum+Pen+Script&family=Nanum+Brush+Script&family=Gaegu:wght@700&family=Hi+Melody&display=swap';

// 거래은행 목록 렌더 위치 (양식 기준 %). 빈 양식이라 □은행명 을 직접 그린다(선택 은행은 ☑).
// 각 은행은 자기 [열,행] 의 BANK_COLX(좌측 시작 left%) · BANK_ROWY(세로 top%) 에 좌측정렬로 배치.
// 열 → 가로(left%). 한 열 전체를 좌우로 옮기려면 여기만 수정.
const BANK_COLX = { 1: 29.5, 2: 38.7, 3: 47.0, 4: 55, 5: 63.5, 6: 73, 7: 82.1 };
// 행 → 세로(top%). 한 행 전체를 위아래로 옮기려면 여기만 수정. (칸 안 4줄, 행간 1.4% 균등)
const BANK_ROWY = { 1: 31.5, 2: 32.9, 3: 34.3, 4: 35.7 };
// 은행명 → [열, 행]. 가로는 BANK_COLX, 세로는 BANK_ROWY 를 참조.
const BANK_POS = {
    '국민은행': [1, 1], '농협': [2, 1], '우리은행': [3, 1], '신한은행': [4, 1], '하나은행': [5, 1], '기업은행': [6, 1], '제일은행': [7, 1],
    '외환은행': [1, 2], '씨티은행': [2, 2], '광주은행': [3, 2], '전북은행': [4, 2], '제주은행': [5, 2], '경남은행': [6, 2], '부산은행': [7, 2],
    '대구은행': [1, 3], '신협': [2, 3], '새마을금고': [3, 3], '우체국': [4, 3], '수협': [5, 3], '케이뱅크': [6, 3], '산업은행': [7, 3],
    '카카오뱅크': [1, 4], '토스뱅크': [2, 4],
};
const BANKS = Object.keys(BANK_POS);

// 입력값 오버레이 위치 (%). align: 'l'(좌측정렬) | 'c'(중앙). hand: 손글씨체.
const POS = {
    customerNo:    { left: 8.6, top: 20.1, align: 'l', pitch: 2.25 }, // pitch: 숫자칸 간격(%)
    startYear:     { left: 58.5, top: 20.1, align: 'c' },
    startMonth:    { left: 63.0, top: 20.1, align: 'c' },
    payDay:        { left: 82.0, top: 20.1, align: 'c' },
    applicantName: { left: 30.0, top: 24.9, align: 'l' },
    phone:         { left: 72.0, top: 24.9, align: 'l' },
    mobile:        { left: 30.0, top: 27.2, align: 'l' },
    email:         { left: 72.0, top: 27.2, align: 'l' },
    address:       { left: 38.0, top: 29.3, align: 'l' },
    account:       { left: 30.0, top: 37.6, align: 'l' },
    rrn:           { left: 30, top: 40.0, align: 'l' },
    holderName:    { left: 30, top: 42.2, align: 'l' },
    applyYear:     { left: 44.0, top: 88.9, align: 'c' },
    applyMonth:    { left: 50.0, top: 88.9, align: 'c' },
    applyDay:      { left: 55.5, top: 88.9, align: 'c' },
    signApplicant: { left: 40.0, top: 92, align: 'l' },
    signHolder:    { left: 68.0, top: 92, align: 'l' },
};
const CHK_CORP = { left: 71.8, top: 42.0 }; // 예금주 구분 □법인
const CHK_PERS = { left: 78.6, top: 42.0 }; // 예금주 구분 □개인

const two = n => String(n).padStart(2, '0');

export default class CmsForm {
    constructor() {
        this.handFont = "'Nanum Pen Script',cursive";
        this.selectedBank = null;
        this.lastHT = '개인';
        this.rrnVals = { '개인': '', '법인': '' };
    }
    destroy() {}

    init() {
        const d = this.data || {};
        this.d = d;
        this.rrnVals['개인'] = d.rrn || '';
        this.rrnVals['법인'] = d.bizNo || ''; // 법인 선택 시 사업자등록번호 자동채움
        this.lastHT = (d.holderType === '법인') ? '법인' : '개인';
        ensureCss(FONT_HREF);
        this.render();
        this.bind();
        this.prefill();
        ensureScript(H2C_SRC); // 모달 열릴 때 미리 로드(다운로드 지연 줄임)
    }

    $(id) { return this.$w.find('#cms_' + id); }
    el(id) { return this.$w.find('#cms_' + id)[0]; }

    /* ───────────────────────── 렌더 ───────────────────────── */
    render() {
        const now = new Date();
        // 이체 개시는 **당월+1** 이 기본이다 — 신청서를 쓰는 달은 출금일(7·8일)이 이미 지났거나
        // 코앞이라 그달부터 개시할 수 없다. Date 가 월을 넘겨주므로 12월이면 해가 같이 넘어간다.
        const start = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        const yOpts = [];
        for (let y = now.getFullYear() - 1; y <= now.getFullYear() + 2; y++)
            yOpts.push(`<option value="${y}" ${y === start.getFullYear() ? 'selected' : ''}>${y}년</option>`);
        const mOpts = [];
        for (let m = 1; m <= 12; m++) mOpts.push(`<option value="${m}" ${m === start.getMonth() + 1 ? 'selected' : ''}>${two(m)}월</option>`);

        const overlays = Object.keys(POS).map(k => {
            const p = POS[k];
            const cls = 'cms-ov' + (p.align === 'c' ? ' c' : '') + (p.hand ? ' hand' : '');
            const style = `left:${p.left}%;top:${p.top}%;`;
            return `<div class="${cls}" id="cms_ov_${k}" style="${style}"></div>`;
        }).join('');

        // 거래은행: 빈 양식이라 목록 전체를 직접 그린다. 좌측정렬 □은행명 (선택 은행은 fill()에서 ☑).
        const bankOverlays = BANKS.map(name => {
            const [c, r] = BANK_POS[name];
            return `<div class="cms-bank" data-bank="${name}" style="left:${BANK_COLX[c]}%;top:${BANK_ROWY[r]}%"><span class="bx">□</span>${name}</div>`;
        }).join('');

        // 스타일은 public/css/a-cms-form.css (평문 CSS) 로 분리됨. edit-v2.blade.php 가 @section('style') 로 싣는다.
        const html = `
<div class="cmsform-wrap">
  <div class="cmsform-form">
    <div class="sect">출금 정보</div>
    <div class="field"><label>고객번호(납부자번호) <span class="opt">숫자 최대 20자리</span></label>
      <input class="inp" id="cms_customerNo" inputmode="numeric" maxlength="20" placeholder="숫자만 (최대 20자리)"></div>
    <div class="field"><label>이체 개시 년 <span class="req">*</span></label>
      <select class="inp" id="cms_startYear">${yOpts.join('')}</select></div>
    <div class="field"><label>이체 개시 월 <span class="req">*</span></label>
      <select class="inp" id="cms_startMonth">${mOpts.join('')}</select></div>
    <div class="field"><label>지정 출금일 <span class="req">*</span></label>
      <div class="radios">
        <div class="radio"><input type="radio" name="cms_payday" id="cms_pd7" value="7" checked><label for="cms_pd7">7일 (스카이)</label></div>
        <div class="radio"><input type="radio" name="cms_payday" id="cms_pd8" value="8"><label for="cms_pd8">8일 (크레인)</label></div>
      </div></div>

    <div class="sect">신청인 정보</div>
    <div class="field"><label>신청인명 <span class="tag">DB</span></label><input class="inp" id="cms_applicantName"></div>
    <div class="field"><label>전화번호 <span class="opt">선택</span></label><input class="inp" id="cms_phone"></div>
    <div class="field"><label>휴대폰 번호 <span class="tag">DB</span></label><input class="inp" id="cms_mobile"></div>
    <div class="field"><label>이메일 <span class="opt">선택</span></label><input class="inp" id="cms_email"></div>
    <div class="field"><label>주소 <span class="opt">선택</span></label><input class="inp" id="cms_address"></div>

    <div class="sect">계좌 정보</div>
    <div class="field"><label>신청계좌 거래은행 <span class="req">*</span></label>
      <input class="inp" id="cms_bankInput" maxlength="6" placeholder="은행명 앞 2글자 (예: 국민)" autocomplete="off">
      <div class="bank-matched" id="cms_bankMatched"><span class="nm" id="cms_bankMatchedName"></span><button type="button" class="re" id="cms_bankReselect">다시 선택</button></div>
      <div class="bank-nomatch" id="cms_bankNomatch">일치 은행이 없어요. 아래에서 선택하세요.</div>
      <button type="button" class="bank-toggle" id="cms_bankToggleBtn">＋ 은행 목록에서 직접 선택</button>
      <div class="bank-grid" id="cms_bankGrid"></div></div>
    <div class="field"><label>출금 계좌번호 <span class="tag">DB</span></label>
      <input class="inp" id="cms_account" inputmode="numeric" placeholder="- 없이 숫자만">
      <div class="hint">'-' 없이 숫자만 (자동으로 숫자만 기입됩니다)</div></div>
    <div class="field"><label>예금주 구분 <span class="req">*</span></label>
      <div class="radios">
        <div class="radio"><input type="radio" name="cms_ht" id="cms_htP" value="개인" checked><label for="cms_htP">개인</label></div>
        <div class="radio"><input type="radio" name="cms_ht" id="cms_htC" value="법인"><label for="cms_htC">법인</label></div>
      </div></div>
    <div class="field"><label id="cms_rrnLabel">예금주 주민번호 앞 6자리 <span class="tag">DB</span></label>
      <input class="inp" id="cms_rrn" placeholder="YYYY-MM-DD">
      <div class="hint" id="cms_rrnHint">생년월일 (YYYY-MM-DD)</div></div>
    <div class="field"><label>예금주명 <span class="req">*</span></label><input class="inp" id="cms_holderName"></div>

    <div class="sect">신청일 · 서명</div>
    <div class="field"><label>신청 년월일</label><input class="inp" id="cms_applyDate" type="date"></div>
    <div class="field"><label>신청인(서명) <span class="req">*</span> <span class="opt">손글씨체로 표기</span></label>
      <input class="inp" id="cms_signName">
      <div class="hint" style="margin-bottom:7px">손글씨 스타일</div>
      <div class="hand-opts" id="cms_handOpts">
        <div class="hand-opt f-pen on" data-f="'Nanum Pen Script',cursive">가나다</div>
        <div class="hand-opt f-brush" data-f="'Nanum Brush Script',cursive">가나다</div>
        <div class="hand-opt f-gaegu" data-f="'Gaegu',cursive">가나다</div>
        <div class="hand-opt f-melody" data-f="'Hi Melody',cursive">가나다</div>
      </div></div>
  </div>

  <div class="cmsform-foot">
    <button type="button" class="btn-dl" id="cms_btnPng">신청서 JPG 다운로드</button>
  </div>

  <div class="cms-capture">
    <div class="paper" id="cms_paper">
      <img id="cms_formImg" src="${FORM_IMG}" crossorigin="anonymous" alt="CMS신청서">
      ${overlays}
      ${bankOverlays}
      <div class="cms-ovcheck" id="cms_ov_chkPers" style="left:${CHK_PERS.left}%;top:${CHK_PERS.top}%">✓</div>
      <div class="cms-ovcheck" id="cms_ov_chkCorp" style="left:${CHK_CORP.left}%;top:${CHK_CORP.top}%">✓</div>
    </div>
  </div>
</div>`;

        this.$w = $(`#${this.id}`);
        this.$w.html(html);
        this.$w = $(`#${this.id}`);
        // 은행 그리드 칩
        const g = this.el('bankGrid');
        BANKS.forEach(b => {
            const el = document.createElement('button');
            el.type = 'button'; el.className = 'bank-chip'; el.textContent = b;
            el.addEventListener('click', () => this.pickBank(b, true));
            g.appendChild(el);
        });
    }

    /* ─────────────────────── DB 값 채우기 ─────────────────────── */
    prefill() {
        const d = this.d;
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${two(today.getMonth() + 1)}-${two(today.getDate())}`;
        this.$('customerNo').val(nz(d.customerNo));
        this.$('applicantName').val(nz(d.applicantName));
        const fmtPhone = v => Util.phoneNumber(nz(v));
        this.$('phone').val(fmtPhone(d.phone));
        this.$('mobile').val(fmtPhone(d.mobile));
        this.$('email').val(nz(d.email));
        this.$('address').val(nz(d.address));
        this.$('account').val(nz(d.account).replace(/[^0-9]/g, ''));
        this.$('holderName').val(nz(d.holderName) || nz(d.applicantName));
        this.$('applyDate').val(nz(d.applyDate) || todayStr);
        this.$('signName').val(nz(d.applicantName) || nz(d.holderName)); // 신청인 서명 = 신청인명
        // 예금주 구분 초기화: 법인이면 자동선택 + 사업자번호 채움 (값 보존 충돌 없이)
        const startCorp = d.holderType === '법인';
        if (startCorp) this.el('htC').checked = true;
        this.lastHT = startCorp ? '법인' : '개인';
        this.$('rrn').val(this.rrnVals[this.lastHT] || '');
        this._setRrnUi(startCorp);
        // 지정 출금일 자동선택: 크레인=8일, 그 외(스카이 등)=7일
        if (d.carType === '크레인') this.el('pd8').checked = true;
        else this.el('pd7').checked = true;
        // 은행 자동 매칭
        const bank = nz(d.bank);
        if (bank) {
            this.$('bankInput').val(bank.slice(0, 2));
            const m = this.matchBank(bank);
            if (m) this.pickBank(m, false);
        }
    }

    /* ───────────────────────── 이벤트 ───────────────────────── */
    bind() {
        const self = this;
        this.$('bankInput').on('input', function () {
            const m = self.matchBank($(this).val());
            if (m) self.pickBank(m, false);
            else self.clearBank($(this).val().trim().length >= 2);
        });
        this.$('bankReselect').on('click', () => self.reselectBank());
        this.$('bankToggleBtn').on('click', () => self.toggleBankGrid());
        this.$w.find('input[name=cms_ht]').on('change', () => self.onHolderType());
        this.$('handOpts').on('click', '.hand-opt', function () {
            self.handFont = $(this).data('f');
            self.$('handOpts').find('.hand-opt').removeClass('on');
            $(this).addClass('on');
        });
        this.$('btnPng').on('click', () => self.downloadJPG());
        // 납부자번호: 숫자만 20자리
        this.$('customerNo').on('input', function () { this.value = this.value.replace(/[^0-9]/g, '').slice(0, 20); });
        // 전화/휴대폰: 공용 전화번호 포맷(common.js phoneNumber)
        $j.Common.Form.registerPhoneHandler('cms_phone');
        $j.Common.Form.registerPhoneHandler('cms_mobile');
    }

    /* ───────────────────────── 은행 ───────────────────────── */
    matchBank(t) {
        t = (t || '').trim();
        if (t.length < 2) return null;
        const s = BANKS.filter(b => b.startsWith(t));
        if (s.length >= 1) return s[0];
        const tw = BANKS.filter(b => b.slice(0, 2) === t.slice(0, 2));
        return tw.length ? tw[0] : null;
    }
    pickBank(name, fromGrid) {
        this.selectedBank = name;
        this.$('bankMatched').css('display', 'flex');
        this.$('bankMatchedName').text(name);
        this.$('bankNomatch').hide();
        if (fromGrid) { this.$('bankInput').val(name); this.closeBankGrid(); }
        this.$w.find('.bank-chip').each(function () { $(this).toggleClass('on', $(this).text() === name); });
    }
    clearBank(showNo) {
        this.selectedBank = null;
        this.$('bankMatched').hide();
        this.$('bankNomatch').css('display', showNo ? 'block' : 'none');
        this.$w.find('.bank-chip').removeClass('on');
    }
    reselectBank() { this.$('bankInput').val(''); this.clearBank(false); this.$('bankInput').focus(); this.openBankGrid(); }
    toggleBankGrid() { this.$('bankGrid').hasClass('open') ? this.closeBankGrid() : this.openBankGrid(); }
    openBankGrid() { this.$('bankGrid').addClass('open'); this.$('bankToggleBtn').text('－ 목록 접기'); }
    closeBankGrid() { this.$('bankGrid').removeClass('open'); this.$('bankToggleBtn').text('＋ 은행 목록에서 직접 선택'); }

    /* ───────────────────────── 예금주 구분 ───────────────────────── */
    onHolderType() {
        const cur = this.el('htC').checked ? '법인' : '개인';
        this.rrnVals[this.lastHT] = this.$('rrn').val(); // 직전 입력값 보존(데이터 안 사라지게)
        this.$('rrn').val(this.rrnVals[cur] || '');
        this.lastHT = cur;
        this._setRrnUi(cur === '법인');
    }

    // 예금주 구분에 따른 rrn 입력칸 라벨/플레이스홀더/길이 제한
    _setRrnUi(corp) {
        this.$('rrnLabel').html(corp ? '사업자등록번호 <span class="tag">DB</span>' : '예금주 주민번호 앞 6자리 <span class="tag">DB</span>');
        this.$('rrnHint').text(corp ? '사업자등록번호 (000-00-00000)' : '생년월일 (YYYY-MM-DD)');
        this.$('rrn').attr('placeholder', corp ? '000-00-00000' : 'YYYY-MM-DD');
        this.$('rrn').attr('maxlength', corp ? 12 : 10); // 법인 000-00-00000(12) / 개인 YYYY-MM-DD(10)
    }

    /* ───────────────────────── 합성 + 다운로드 ───────────────────────── */
    fill() {
        const set = (k, v) => this.$('ov_' + k).text(v);
        this.setBoxed('ov_customerNo', this.$('customerNo').val(), POS.customerNo.pitch);
        set('startYear', String(this.$('startYear').val()).slice(2)); // 2자리
        set('startMonth', this.$('startMonth').val() ? two(this.$('startMonth').val()) : '');
        set('payDay', this.$w.find('input[name=cms_payday]:checked').val() || '');
        set('applicantName', this.$('applicantName').val());
        set('phone', this.$('phone').val());
        set('mobile', this.$('mobile').val());
        set('email', this.$('email').val());
        set('address', this.$('address').val());
        set('account', this.$('account').val().replace(/[^0-9]/g, ''));
        set('rrn', this.rrnText());
        set('holderName', this.$('holderName').val());

        const ad = this.$('applyDate').val();
        if (ad) { const [y, m, dd] = ad.split('-'); set('applyYear', y); set('applyMonth', String(+m)); set('applyDay', String(+dd)); }
        else { set('applyYear', ''); set('applyMonth', ''); set('applyDay', ''); }

        // 하단 동의란: [인쇄된 이름] + [손글씨 서명] 을 한 줄에 (손글씨는 오른쪽)
        this.setSign('ov_signApplicant', this.$('applicantName').val(), this.$('signName').val());
        this.setSign('ov_signHolder', this.$('holderName').val(), this.$('holderName').val());

        // 거래은행: 선택 은행만 색칠된 네모 ■(.on 스타일로 강조), 나머지는 빈 네모 □
        this.$w.find('.cms-bank').each((i, el) => {
            const on = el.getAttribute('data-bank') === this.selectedBank;
            el.querySelector('.bx').textContent = on ? '■' : '□';
            el.classList.toggle('on', on);
        });
        // 예금주 구분 체크
        this.$('ov_chkPers').css('display', this.el('htP').checked ? 'block' : 'none');
        this.$('ov_chkCorp').css('display', this.el('htC').checked ? 'block' : 'none');
    }

    // 칸칸 박스: 각 글자를 동일 폭(pitch%) 칸에 가운데정렬로 배치 (양식 숫자칸 간격 맞춤)
    setBoxed(id, text, pitchPct) {
        const w = (pitchPct || 2) * 10; // 양식폭 1000px 기준: 1% = 10px
        this.$(id).html(String(text == null ? '' : text).split('').map(c =>
            `<span style="display:inline-block;width:${w}px;text-align:center">${c}</span>`).join(''));
    }

    // 주민번호 앞 6자리(YYMMDD). 개인: 1996-07-12 → 960712 / 법인: 사업자번호 그대로
    rrnText() {
        const v = (this.$('rrn').val() || '').trim();
        if (this.el('htC').checked) return v;
        const m = v.match(/^\d{2}(\d{2})-?(\d{2})-?(\d{2})$/);
        if (m) return m[1] + m[2] + m[3];
        return v.replace(/[^0-9]/g, '').slice(-6);
    }

    // 동의란 한 줄: 인쇄 이름(좌) + 손글씨 서명(우)
    setSign(id, printName, handName) {
        const $el = this.$(id).empty();
        if (printName) $el.append($('<span>').css({ 'font-family': "'Pretendard',sans-serif", 'font-size': '19px', color: '#111' }).text(printName));
        if (handName) $el.append($('<span>').css({ 'font-family': this.handFont, 'font-size': '27px', color: '#1a2a6c', 'margin-left': '18px' }).text(handName));
    }

    // 자동입력(DB) 필수항목 중 비어있는 항목명 목록
    _missingRequired() {
        const fields = [
            ['applicantName', '신청인명'],
            ['mobile', '휴대폰 번호'],
            ['account', '출금 계좌번호'],
            ['holderName', '예금주명'],
            ['rrn', this.el('htC').checked ? '사업자등록번호' : '예금주 주민번호'],
        ];
        const miss = fields.filter(([id]) => !String(this.$(id).val() || '').trim()).map(([, label]) => label);
        if (!this.selectedBank) miss.unshift('신청계좌 거래은행');
        return miss;
    }

    // 버튼 클릭: 비어있는 항목이 있으면 "그래도 다운로드?" 확인 후 진행
    downloadJPG() {
        const miss = this._missingRequired();
        if (!miss.length) { this._generateJPG(); return; }
        const msg = '비어있는 항목: ' + miss.join(', ') + ' — 그래도 이미지를 다운로드할까요?';
        Util.swalert(['비어있는 항목이 있습니다', 'warning', msg], () => this._generateJPG(), ['예', '아니오']);
    }

    async _generateJPG() {
        const btn = this.$('btnPng');
        btn.prop('disabled', true).text('생성 중…');
        try {
            this.fill();
            await ensureScript(H2C_SRC);
            await ensureCssLoaded(FONT_HREF);
            if (document.fonts && document.fonts.ready) await document.fonts.ready;
            await imgReady(this.el('formImg'));
            if (!this.el('formImg').naturalWidth) throw new Error('양식 이미지를 불러오지 못했습니다. (이미지 경로 확인)');
            await new Promise(r => setTimeout(r, 120));
            const canvas = await window.html2canvas(this.el('paper'), { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false });
            const a = document.createElement('a');
            a.download = 'CMS자동이체신청서_' + (this.$('applicantName').val() || '신청서') + '.jpg';
            a.href = canvas.toDataURL('image/jpeg', 1.0);
            a.click();
        } catch (e) {
            alert('다운로드 실패: ' + (e && e.message ? e.message : e));
        } finally {
            btn.prop('disabled', false).text('신청서 JPG 다운로드');
        }
    }
}

/* ───────────────────────── 유틸 ───────────────────────── */
function nz(v) { return (v === undefined || v === null) ? '' : String(v); }

function ensureScript(src) {
    return new Promise((resolve, reject) => {
        if (window.html2canvas) return resolve();
        let s = document.querySelector(`script[data-cms-h2c]`);
        if (s) { s.addEventListener('load', () => resolve()); s.addEventListener('error', reject); return; }
        s = document.createElement('script');
        s.src = src; s.async = true; s.setAttribute('data-cms-h2c', '1');
        s.onload = () => resolve(); s.onerror = reject;
        document.head.appendChild(s);
    });
}
function ensureCss(href) {
    if (document.querySelector(`link[data-cms-font]`)) return;
    const l = document.createElement('link');
    l.rel = 'stylesheet'; l.href = href; l.setAttribute('data-cms-font', '1');
    document.head.appendChild(l);
}
function ensureCssLoaded(href) {
    return new Promise(resolve => {
        ensureCss(href);
        const l = document.querySelector(`link[data-cms-font]`);
        if (!l || l.sheet) return resolve();
        l.addEventListener('load', () => resolve());
        setTimeout(resolve, 800);
    });
}
function imgReady(img) {
    return new Promise(resolve => {
        if (!img) return resolve();
        if (img.complete && img.naturalWidth) return resolve();
        img.addEventListener('load', () => resolve());
        img.addEventListener('error', () => resolve());
    });
}